import React, { Dispatch, SetStateAction, useState } from "react";
import {
  BillableElement,
  Board,
  boardHasBeenPaidFor,
  SelectedElement,
} from "../../../../../reducers/uploadedBoard";
import { useBoardFromContext } from "../../../../../utils/hooks/commonly-stored/uploaded-board/board";
import { useUpdateBoardElementSelection } from "../../../../../utils/hooks/commonly-stored/uploaded-board/updates/billOfMaterials";
import Checkbox from "../../../../common/Checkbox";
import { Actions } from "./ElementsRowStyles";
import FlashPCBTooltip from "../../../../common/FlashPCBTooltip";
import Icon from "../../../../common/Icon";
import { Pill, TableCell, TableRow } from "../../../../../constants/Styles";
import PartSelection from "../common/PartSelection";
import _ from "lodash";
import SelectPartButton from "../common/SelectPartButton";
import DeleteElementButton from "../common/DeleteElementButton";
import usePart from "../../../../../utils/hooks/commonly-stored/parts";
import PlacementCells from "./PlacementCells";
import ExpandRowsToggle from "./ExpandRowsToggle";
import { useAppSelector } from "../../../../../utils/hooks/store";
import { ADMIN_GROUP, ENGINEER_GROUP } from "../../../../../utils/group";
import {
  BLANK,
  useGetElementAlert,
} from "../../../../../utils/hooks/commonly-stored/uploaded-board/billOfMaterials";

export default function ElementsRow({
  elements,
  setSelectedElement,
  disableEditing,
}: {
  elements: BillableElement[];
  setSelectedElement: Dispatch<SetStateAction<SelectedElement | null>>;
  disableEditing: boolean;
}) {
  const board = useBoardFromContext().board as Board;
  const updateBoardElementSelection = useUpdateBoardElementSelection();
  const [groupIsExpanded, setGroupIsExpanded] = useState(false);
  const { part, loading } = usePart(elements[0]!.part_id);
  const { groups } = useAppSelector((state) => state.auth);
  const isAdminOrEngineer =
    groups.includes(ADMIN_GROUP) || groups.includes(ENGINEER_GROUP);
  const paidBoard = boardHasBeenPaidFor(board);
  const getElementAlert = useGetElementAlert(board);

  const renderCheckbox = (
    element: BillableElement,
    newValue: { [key: string]: boolean },
    checked: boolean,
  ) => {
    const onChange = () => {
      if (groupIsExpanded) {
        updateBoardElementSelection([
          {
            elementName: element.name,
            elementId: element.id,
            doNotPopulate: element.do_not_populate,
            part_id: element.part_id,
            isConsignment: element.is_consignment,
            lockAllocation: element.lock_allocation,
            elementPlacement: null,
            ...newValue,
          },
        ]);
      } else {
        updateBoardElementSelection(
          elements.map((element) => ({
            elementName: element.name,
            elementId: element.id,
            doNotPopulate: element.do_not_populate,
            part_id: element.part_id,
            isConsignment: element.is_consignment,
            lockAllocation: element.lock_allocation,
            elementPlacement: null,
            ...newValue,
          })),
        );
      }
    };
    return (
      <Checkbox
        checked={checked}
        onChange={onChange}
        disabled={disableEditing}
      />
    );
  };

  const getNameCellContent = (element: BillableElement) => {
    return groupIsExpanded
      ? element.name
      : elements.map((element) => element.name).join(", ");
  };

  const elementsToDisplay = groupIsExpanded ? elements : [elements[0]!];

  const dnpAll = elements.every((element) => element.do_not_populate);
  const alertAll = elements.some((element) => getElementAlert(element));

  return (
    <>
      {elementsToDisplay.map((element, groupIndex) => {
        const dnp = groupIsExpanded ? element.do_not_populate : dnpAll;
        const alert = getElementAlert(element);

        const elementHasAlert = groupIsExpanded ? alert !== null : alertAll;

        const alertNode = elementHasAlert ? (
          <FlashPCBTooltip content={alert}>
            <Icon src="/icons/alert_red.svg" alt="alert" />
          </FlashPCBTooltip>
        ) : null;

        const minimumOrderQuantityWarning =
          ((!element.do_not_populate && part?.price_info?.quantity) || 1) >
          1 ? (
            <FlashPCBTooltip
              content={`This part has a minimum order quantity of ${
                part?.price_info?.quantity || 1
              } that could affect your quote price.`}
            >
              <Icon src="/icons/alert_yellow.svg" alt="alert" />
            </FlashPCBTooltip>
          ) : null;

        const expandRowToggle = (
          <ExpandRowsToggle
            groupIndex={groupIndex}
            groupLength={elements.length}
            groupIsExpanded={groupIsExpanded}
            setGroupIsExpanded={setGroupIsExpanded}
          />
        );

        return (
          <TableRow
            key={`${element.name}-${groupIndex}`}
            warn={dnp}
            alert={!paidBoard && elementHasAlert}
          >
            <TableCell textAlign={"center"}>{expandRowToggle}</TableCell>
            <TableCell>{getNameCellContent(element)}</TableCell>
            <TableCell>{groupIndex === 0 ? elements.length : ""}</TableCell>
            <TableCell>{element.value_text || BLANK}</TableCell>
            <TableCell>{element.footprint_name}</TableCell>
            <PlacementCells
              element={element}
              elementsInGroup={elements}
              disableEditing={disableEditing}
              groupIsExpanded={groupIsExpanded}
              expandRowToggle={expandRowToggle}
            />
            <TableCell>
              {loading ? (
                <Pill />
              ) : (
                <PartSelection element={element} part={part} />
              )}
            </TableCell>
            <TableCell>
              {loading ? <Pill /> : part?.manufacturer || BLANK}
            </TableCell>
            <TableCell>
              {loading ? (
                <Pill />
              ) : (
                _.truncate(part?.short_description || BLANK, {
                  length: 200,
                })
              )}
            </TableCell>
            <TableCell>
              {renderCheckbox(
                element,
                { doNotPopulate: !element.do_not_populate },
                !element.do_not_populate,
              )}
            </TableCell>
            <TableCell>
              {renderCheckbox(
                element,
                { isConsignment: !element.is_consignment },
                element.is_consignment,
              )}
            </TableCell>
            <TableCell>
              <Actions>
                <SelectPartButton
                  element={element}
                  setSelectedElement={setSelectedElement}
                  disableEditing={disableEditing}
                />
                <DeleteElementButton
                  elements={groupIsExpanded ? [element] : elements}
                  disableEditing={disableEditing}
                />
                {!paidBoard && (
                  <>
                    {alertNode}
                    {minimumOrderQuantityWarning}
                  </>
                )}
              </Actions>
            </TableCell>
            {isAdminOrEngineer && (
              <TableCell>
                {renderCheckbox(
                  element,
                  { lockAllocation: !element.lock_allocation },
                  element.lock_allocation,
                )}
              </TableCell>
            )}
          </TableRow>
        );
      })}
    </>
  );
}
